import React, { useCallback } from 'react';
import { useSetState } from 'react-use';
import { makeStyles } from '@mui/styles';

import _ from 'lodash';
import { Grid, TextInput, IconButton, Icon } from '@ts/storybook';
import Tooltip from '@components/Outputs/Tooltip';
import { JSONValidator } from '@components/Inputs';
import GeometryDrawer from '@components/Maps/Map/GeometryDrawer';
import { CElement, CGeometry, IForm, IGeometry, TMode } from '@ts/ui-models';
import { GridContainerFullHeight, Switch } from '@ts/storybook';

const useStyles = makeStyles(() => ({
  containerLeftBtnMap: { display: 'flex', position: 'absolute', left: '40px', top: '8px', zIndex: 2 },
  containerRightBtnMap: { display: 'flex', position: 'absolute', right: '2px', top: '8px', zIndex: 2 },
  iconBtnMap: (props: any) => ({
    backgroundColor: 'rgba(0, 60, 136, 0.5)',
    color: '#fff',
    textAlign: 'center',
    border: props.isViewSatellite ? '2px solid rgba(255, 255, 255, 0.4)' : '2px solid rgba(255, 255, 255, 0.7)',
    borderRadius: '4px',
    padding: '2px',
    cursor: 'pointer',
    marginRight: '5px',
    height: '32px',
    width: '32px',
    display: 'flex',
    position: 'absolute',
    zIndex: 1,
    right: '75px',
    top: '9px',
  }),
  iconMap: { fontSize: '24px', opacity: 0.8 },
  tooltip: { position: 'absolute', zIndex: 1, right: '40px', top: '10px' },
}));

interface ILocationElementProps extends IForm {
  value: CElement;
}

const GTLocationElement: React.FC<ILocationElementProps> = props => {
  const { values } = props;
  const tGeometry = new CGeometry(values.flavors?.location?.geometry);
  console.log('tGeometry', tGeometry);
  const isViewMode = props.mode === 'view';
  const [state, setState] = useSetState({
    isSeeCoordinates: false,
  });

  //#region Handlers
  const onChangeGeometry = async (key: keyof CGeometry, value: any) => {
    await updateTGeometry(key, value);
    props.onChange('flavors.location.geometry', tGeometry);
  };
  const onClickSeeCoordinates = () => {
    setState({ isSeeCoordinates: !state.isSeeCoordinates });
  };
  //#endregion

  //#region Functions
  const updateTGeometry = async (key: keyof CGeometry, value: any) => {
    tGeometry[key] = value;
    if (key === 'type' || key === 'dim') {
      value = tGeometry.type + tGeometry.dim;
      tGeometry['geometryTypeId'] = value;
    }
    if (key === 'geomGpsCoord' || key === 'geomLocalRectCoord') {
      if (typeof value === 'object' && !Array.isArray(value)) {
        tGeometry[key] = value;
      } else {
        tGeometry[key] = { type: '', coordinates: [] };
        tGeometry[key].type = tGeometry.typeDrawer;
        tGeometry[key].coordinates = tGeometry.getTypeDrawer === 'polygon' ? [value] : value;
      }
    }
  };

  //#endregion

  //#region Render
  const renderSwitchesGeometryType = () => {
    if (isViewMode) return null;
    return (
      <Grid container direction="row" size={{ xs: 12 }}>
        <Switch
          checked={Boolean(tGeometry.type === 'point')}
          onChangeValue={v => {
            onChangeGeometry('type', v ? 'point' : 'polygon');
          }}
          leftLabel={'Polygon'}
          rightLabel={'Point'}
          isReadOnly={isViewMode}
        />
        <Switch
          checked={Boolean(tGeometry.dim === '3')}
          onChangeValue={v => {
            onChangeGeometry('dim', v ? '3' : '2');
          }}
          leftLabel={'2D'}
          rightLabel={'3D'}
          isReadOnly={isViewMode}
        />
      </Grid>
    );
  };
  const renderZDimensionGeometry = () => {
    if (tGeometry.getDimension === '2') return;
    if (tGeometry.getDimension === '3' && tGeometry.type === 'polygon') {
      return (
        <Grid container spacing={2} pb={1} direction="row" size={{ xs: 'auto' }}>
          <Grid size={{ xs: 6 }}>
            <TextInput
              label="Top Z coordinate"
              value={tGeometry.topZ}
              type="number"
              onChangeValue={v => onChangeGeometry('topZ', v)}
              isReadOnly={isViewMode}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextInput
              label="Bottom Z coordinate"
              value={tGeometry.bottomZ}
              type="number"
              onChangeValue={v => onChangeGeometry('bottomZ', v)}
              isReadOnly={isViewMode}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container direction={'column'} pb={1}>
          <TextInput
            label="Point Z coordinate"
            value={tGeometry.pointZ}
            type="number"
            onChangeValue={v => onChangeGeometry('pointZ', v)}
            isReadOnly={isViewMode}
          />
        </Grid>
      );
    }
  };

  const renderMap = useCallback(() => {
    if (tGeometry) {
      return <GeometryDrawer {...props} geometry={tGeometry} onChangeGeometry={onChangeGeometry} disabled={isViewMode} />;
    }

    return null;
  }, [tGeometry]);

  const renderManualyCoordinates = () => {
    return (
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <div style={{ overflow: 'auto', position: 'absolute', width: '100%', height: '100%', paddingTop: '20px' }}>
          <Grid size={{ xs: 12 }}>
            <JSONValidator
              label={'Geometry in EPSG4326 coordinates'}
              value={tGeometry.geomGpsCoord}
              onJSONValidated={v => onChangeGeometry('geomGpsCoord', v)}
              disabled={isViewMode}
            />
          </Grid>
          <Grid size={{ xs: 12 }} mt={2}>
            <JSONValidator
              label={'Geometry in local coordinates'}
              value={tGeometry.geomLocalRectCoord}
              onJSONValidated={v => onChangeGeometry('geomLocalRectCoord', v)}
              disabled={isViewMode}
            />
          </Grid>
        </div>
      </div>
    );
  };
  //#endregion

  const classes = useStyles({ ...state });
  return (
    <GridContainerFullHeight id={'general-tab-location-element'}>
      {!isViewMode && <Grid size={'auto'}>{renderSwitchesGeometryType()}</Grid>}
      <Grid size={'auto'}>{renderZDimensionGeometry()}</Grid>
      <Grid
        size={'grow'}
        sx={{
          height: isViewMode ? (tGeometry.getDimension !== '3' ? '100%' : 'calc(100% - 45px)') : 'calc(100% - 40px)',
        }}
      >
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
          <Tooltip title={!state.isSeeCoordinates ? 'See coordinates in JSON' : 'See coordinates in map'} isInRight>
            <IconButton className={classes.iconBtnMap} onClick={onClickSeeCoordinates}>
              <Icon icon={!state.isSeeCoordinates ? 'dataObject' : 'map'} size={20} className={classes.iconMap} />
            </IconButton>
          </Tooltip>
          {!state.isSeeCoordinates ? renderMap() : renderManualyCoordinates()}
        </div>
      </Grid>
    </GridContainerFullHeight>
  );
};

export default GTLocationElement;
